import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { themesAPI, ThemeLibraryItem } from "@/lib/api";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

const DOMAIN_ORDER = ["Executing", "Influencing", "Relationship Building", "Strategic Thinking"];

export default function AdminStrengthsLeaderboard() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [themes, setThemes] = useState<ThemeLibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState<string>("all");

  useEffect(() => {
    const loadThemes = async () => {
      setIsLoading(true);
      try {
        const response = await themesAPI.getThemes();
        setThemes(response.data || []);
      } catch (error: any) {
        toast({
          title: t("strengthsExplorer.themesLoadFailed"),
          description: getLocalizedErrorMessage(error, "strengthsExplorer.themesLoadFailedDesc"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadThemes();
  }, [t, toast]);

  const filteredThemes = useMemo(
    () => (domainFilter === "all" ? themes : themes.filter((theme) => theme.domain === domainFilter)),
    [themes, domainFilter]
  );

  return (
    <div className="min-w-0 space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div className="min-w-0">
        <h2 className="break-words text-xl font-bold mb-2 sm:text-2xl">
          {t("strengthsExplorer.adminLeaderboardTitle")}
        </h2>
        <p className="text-muted-foreground">
          {t("strengthsExplorer.adminLeaderboardSubtitle")}
        </p>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("strengthsExplorer.loadingThemes")}
        </Card>
      ) : (
        <>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
            <button
              type="button"
              onClick={() => setDomainFilter("all")}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                domainFilter === "all"
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t("strengthsExplorer.allDomains")}
            </button>
            {DOMAIN_ORDER.map((domain) => {
              const color = STRENGTHS_EXPLORER_DOMAIN_COLORS[domain];
              const isActive = domainFilter === domain;
              return (
                <button
                  key={domain}
                  type="button"
                  onClick={() => setDomainFilter(domain)}
                  className="shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"
                  style={
                    isActive
                      ? { backgroundColor: color, borderColor: color, color: "#fff" }
                      : { backgroundColor: `${color}10`, borderColor: `${color}40`, color }
                  }
                >
                  {translateDomainName(domain, t, i18n.language)}
                </button>
              );
            })}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredThemes.map((theme) => {
              const color = STRENGTHS_EXPLORER_DOMAIN_COLORS[theme.domain];
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => navigate(`/admin/leaderboard/${theme.id}`)}
                  className="rounded-lg text-start focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <Card
                    className="h-full border-s-4 p-4 transition-all hover:border-primary/40 hover:shadow-md"
                    style={{ borderInlineStartColor: color, borderInlineStartWidth: 4 }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="min-w-0 truncate font-semibold">
                        {translateStrengthName(theme.name, t, i18n.language)}
                      </p>
                      <Trophy className="h-4 w-4 shrink-0" style={{ color }} />
                    </div>
                    <Badge
                      className="mt-2 border text-xs"
                      style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                    >
                      {translateDomainName(theme.domain, t, i18n.language)}
                    </Badge>
                  </Card>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

